import { Capacitor } from '@capacitor/core';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useFamilyStatus } from '../../contexts/FamilyStatusContext';
import Button from '../UI/Button';
import InfoTooltip from '../UI/InfoTooltip';
import Card from '../UI/Card';

import {
  supabase,
} from '../../services/supabase';


const PlayBillingPanel = React.lazy(() => import('./PlayBillingPanel'));

const AppleStoreKitPanel = React.lazy(() => import('./AppleStoreKitPanel'));

const PLAN_DETAILS = {
  family: {
    name: 'MyPiggy Family',
    limit: 3,
    monthly: '$2.99/month',
    annual: '$29.99/year',
  },
  family_plus: {
    name: 'MyPiggy Family Plus',
    limit: 5,
    monthly: '$3.99/month',
    annual: '$39.99/year',
  },
};

const TERMINAL_PROVIDER_STATUSES = new Set([
  'canceled',
  'incomplete_expired',
]);

const formatDate = (value) => {
  if (!value) return '—';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
  }).format(new Date(value));
};

const formatStatus = (value) => {
  if (!value) return 'Not subscribed';

  return value
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};


const getSubscriptionLabel = (summary) => {
  const plan = PLAN_DETAILS[summary?.plan]?.name || 'MyPiggy';
  const interval = summary?.billing_interval
    ? formatStatus(summary.billing_interval)
    : '';

  return interval ? `${plan} ${interval}` : plan;
};

const getPersistentMessage = (summary) => {
  const subscription = getSubscriptionLabel(summary);
  const organizer = summary.family_organizer_name || 'your Family Organizer';
  const isOrganizer = summary.can_manage_billing === true;
  const trialDate = formatDate(summary.trial_ends_at);
  const periodDate = formatDate(summary.current_period_ends_at);
  const cancellationDate = formatDate(summary.cancellation_effective_at);
  const graceDate = formatDate(summary.grace_ends_at);
  const pendingDate = formatDate(summary.pending_change_effective_at);
  const pendingPlan = PLAN_DETAILS[summary.pending_plan]?.name
    || PLAN_DETAILS[summary.plan]?.name
    || 'MyPiggy';
  const pendingInterval = summary.pending_billing_interval
    ? formatStatus(summary.pending_billing_interval)
    : formatStatus(summary.billing_interval);

  switch (summary.status_code) {
    case 'trial_unsubscribed':
      return isOrganizer
        ? `Your free trial ends on ${trialDate}. You can subscribe now; billing starts when checkout succeeds and unused trial days end.`
        : `Your family's free trial ends on ${trialDate}. Only your Family Organizer, ${organizer}, can start a paid subscription.`;

    case 'trial_subscribed': {
      const price = PLAN_DETAILS[summary.plan]?.[summary.billing_interval];
      return isOrganizer
        ? `You're subscribed to ${subscription}. Your free trial continues through ${trialDate}. You can start paid billing now${price ? ` at ${price}` : ''}; unused trial days will end.`
        : `Your family is subscribed to ${subscription}. The free trial continues through ${trialDate}, unless your Family Organizer starts paid billing sooner.`;
    }

    case 'trial_cancellation_scheduled':
      return isOrganizer
        ? `Your ${subscription} subscription is canceled. Your current plan remains available through ${cancellationDate}, and you will not be charged. Your family will become read-only afterward.`
        : `Your family's ${subscription} subscription is canceled but remains available through ${cancellationDate}. Contact your Family Organizer, ${organizer}, if your family wants to continue using MyPiggy.`;

    case 'cancellation_scheduled':
      return isOrganizer
        ? `Your ${subscription} subscription is canceled but remains available through ${cancellationDate}. A 15-day grace period with full access follows, then your family becomes read-only.`
        : `Your family's ${subscription} subscription remains available through ${cancellationDate}, followed by a 15-day grace period with full access. Contact your Family Organizer, ${organizer}, to continue using MyPiggy afterward.`;

    case 'change_scheduled':
      return `${subscription} remains active through ${pendingDate}. ${pendingPlan} ${pendingInterval} will begin on that date.`;

    case 'payment_grace':
      if (summary.billing_provider === 'apple'
          && summary.provider_subscription_status === 'APPLE_EXPIRED') {
        return isOrganizer
          ? `Your ${subscription} subscription has ended. Your family has full MyPiggy access through ${graceDate}. Subscribe again before then to avoid read-only access.`
          : `Your family's ${subscription} subscription has ended. Full MyPiggy access continues through ${graceDate}. Contact your Family Organizer, ${organizer}, to renew it.`;
      }
      if (summary.billing_provider === 'google_play'
          && summary.provider_subscription_status === 'SUBSCRIPTION_STATE_EXPIRED') {
        return isOrganizer
          ? `Your ${subscription} Google Play subscription has ended. Your family retains full MyPiggy access through ${graceDate}. Subscribe again before then to avoid read-only access.`
          : `Your family's ${subscription} Google Play subscription has ended. Full MyPiggy access continues through ${graceDate}. Contact your Family Organizer, ${organizer}, to renew it.`;
      }
      if (summary.billing_provider === 'google_play'
          && summary.provider_subscription_status === 'SUBSCRIPTION_STATE_ACTIVE') {
        return isOrganizer
          ? `Your Google Play subscription's last verified billing period has ended. Your family retains full MyPiggy access through ${graceDate}. We're awaiting an updated subscription status from Google Play.`
          : `Your family's last verified Google Play billing period has ended. Full MyPiggy access continues through ${graceDate} while the latest subscription status is checked. Contact your Family Organizer, ${organizer}, if needed.`;
      }
      if (summary.is_cancellation_scheduled) {
        return isOrganizer
          ? `Your ${subscription} paid period ended on ${cancellationDate}. Your family has full access during the 15-day cancellation grace period through ${graceDate}, then becomes read-only.`
          : `Your family's ${subscription} paid period ended on ${cancellationDate}. Full access continues through the cancellation grace period ending ${graceDate}. Contact your Family Organizer, ${organizer}, to continue afterward.`;
      }
      return isOrganizer
        ? `We couldn't process the payment for your ${subscription} subscription. Your 15-day grace period ends on ${graceDate}. Update your payment method before that date to prevent your family from becoming read-only.`
        : `The payment for your family's ${subscription} subscription needs attention. Full access continues through the 15-day grace period ending ${graceDate}. Please contact your Family Organizer, ${organizer}.`;

    case 'expired':
      if (isOrganizer && summary.active_child_count > PLAN_DETAILS.family.limit) {
        return `Your ${subscription} subscription has ended, and your family is read-only. You currently have ${summary.active_child_count} active children. Restart Family Plus or reduce the family to 3 active children before selecting Family.`;
      }

      return isOrganizer
        ? `Your ${subscription} subscription has ended. Your family is now read-only. Restart a subscription to restore full access.`
        : `Your family's ${subscription} subscription is no longer active, and the family is now read-only. Please contact your Family Organizer, ${organizer}.`;

    case 'active':
      return isOrganizer
        ? `Your ${subscription} subscription is active. Your next billing date is ${periodDate}.`
        : `Your family's ${subscription} subscription is active. Only your Family Organizer, ${organizer}, can manage it.`;

    default:
      return 'Your family subscription status is shown below.';
  }
};

const getPrimaryPill = (summary) => {
  if (summary.effective_status === 'grace') return 'Grace Period';
  if (summary.effective_status === 'expired') return 'Read Only';
  if (summary.effective_status === 'trial') return 'Free Trial';
  if (summary.effective_status === 'active') return 'Active';
  return formatStatus(summary.effective_status);
};

const newRequestId = () => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    (character) => {
      const random = Math.floor(Math.random() * 16);
      const value = character === 'x'
        ? random
        : (random & 0x3) | 0x8;

      return value.toString(16);
    }
  );
};

const readFunctionError = async (error, fallback) => {
  try {
    const payload = await error?.context?.json?.();
    return payload?.error || fallback;
  } catch {
    return error?.message || fallback;
  }
};


export default function FamilyBillingCard() {
  const { refreshFamilyAccessState } = useFamilyStatus();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('family');
  const [selectedInterval, setSelectedInterval] = useState('monthly');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadSummary = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);

    try {
      const {
        data,
        error: summaryError,
      } = await supabase.rpc(
        'get_my_family_billing_summary'
      );

      if (summaryError) throw summaryError;

      if (!Array.isArray(data) || data.length !== 1) {
        throw new Error(
          'Unable to determine the family billing status.'
        );
      }

      const nextSummary = data[0];
      setSummary(nextSummary);
      setSelectedPlan(nextSummary.plan || 'family');
      setSelectedInterval(
        nextSummary.billing_interval || 'monthly'
      );
      return nextSummary;
    } catch (loadError) {
      console.error('Unable to load billing summary:', loadError);
      setError(
        loadError?.message ||
          'Unable to load family billing information.'
      );
      return null;
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const refreshAfterProviderPurchase = useCallback(async () => {
    const [nextSummary, access] = await Promise.all([
      loadSummary({ quiet: true }),
      refreshFamilyAccessState(),
    ]);
    if (!nextSummary || !access) {
      throw new Error('Purchase verified, but access could not be refreshed. Tap Restore Purchases to retry.');
    }
    return { summary: nextSummary, access };
  }, [loadSummary, refreshFamilyAccessState]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let active = true;

    const refreshNativeBilling = async () => {
      if (!active || document.visibilityState === 'hidden') return;

      try {
        await refreshAfterProviderPurchase();
      } catch (refreshError) {
        console.warn(
          'Unable to refresh native billing status:',
          refreshError
        );
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshNativeBilling();
      }
    };

    const handleFocus = () => {
      refreshNativeBilling();
    };

    const intervalId = window.setInterval(
      refreshNativeBilling,
      15000
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );
    window.addEventListener('focus', handleFocus);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshAfterProviderPurchase]);

  useEffect(() => {
    const handlePageShow = (event) => {
      setBusyAction('');

      if (event.persisted) {
        loadSummary({ quiet: true });
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [loadSummary]);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const billingResult = search.get('billing');

    if (billingResult === 'success') {
      setMessage(
        'Checkout completed. Your subscription status will update shortly.'
      );
    } else if (billingResult === 'cancelled') {
      setMessage('Checkout was cancelled. No subscription change was made.');
    } else if (billingResult === 'portal-return') {
      setMessage('Billing portal closed. Your latest billing status is shown below.');
    } else {
      return;
    }

    search.delete('billing');

    const nextSearch =
      search.toString();

    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`
    );
  }, []);

  const invokeFunction = async (name, body, fallback) => {
    const {
      data,
      error: functionError,
    } = await supabase.functions.invoke(name, { body });

    if (functionError) {
      throw new Error(
        await readFunctionError(functionError, fallback)
      );
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return data;
  };

  const handleCheckout = async () => {
    if (busyAction) return;

    try {
      setBusyAction('checkout');
      setError('');
      setMessage('');

      const data = await invokeFunction(
        'create_stripe_checkout',
        {
          plan: selectedPlan,
          billingInterval: selectedInterval,
          requestId: newRequestId(),
        },
        'Unable to start Stripe Checkout.'
      );

      if (!data?.checkoutUrl) {
        throw new Error('Stripe Checkout did not return a valid URL.');
      }

      window.location.assign(data.checkoutUrl);
    } catch (checkoutError) {
      console.error('Stripe Checkout error:', checkoutError);
      setError(
        checkoutError?.message ||
          'Unable to start Stripe Checkout.'
      );
      setBusyAction('');
    }
  };

  const handleStartPaidNow = async () => {
    if (busyAction || !summary || summary.provider_subscription_status !== 'trialing') return;
    const price = PLAN_DETAILS[summary.plan]?.[summary.billing_interval];
    if (!window.confirm(`Start your paid ${getSubscriptionLabel(summary)} subscription now${price ? ` at ${price}` : ''}? Stripe will begin billing and your unused trial days will end.`)) return;
    try {
      setBusyAction('start-paid');
      setError('');
      setMessage('');
      await invokeFunction('change_stripe_subscription', {
        action: 'start_paid_now',
        plan: summary.plan,
        billingInterval: summary.billing_interval,
        requestId: newRequestId(),
      }, 'Unable to start paid billing.');
      setMessage('Your paid subscription is starting. Your billing status will update when Stripe confirms it.');
      await loadSummary({ quiet: true });
    } catch (cause) {
      setError(cause.message || 'Unable to start paid billing.');
    } finally {
      setBusyAction('');
    }
  };
  const handlePortal = async () => {
    if (busyAction) return;

    try {
      setBusyAction('portal');
      setError('');
      setMessage('');

      const data = await invokeFunction(
        'create_stripe_portal',
        {
          requestId: newRequestId(),
        },
        'Unable to open the Stripe billing portal.'
      );

      if (!data?.portalUrl) {
        throw new Error('Stripe did not return a valid billing portal URL.');
      }

      window.location.assign(data.portalUrl);
    } catch (portalError) {
      console.error('Stripe portal error:', portalError);
      setError(
        portalError?.message ||
          'Unable to open the Stripe billing portal.'
      );
      setBusyAction('');
    }
  };

  const handleSubscriptionChange = async () => {
    if (busyAction || !summary) return;

    const planChanges = selectedPlan !== summary.plan;
    const intervalChanges =
      selectedInterval !== summary.billing_interval;
    const hasPendingChange = Boolean(
      summary.pending_plan ||
      summary.pending_billing_interval
    );
    const matchesPendingChange =
      hasPendingChange &&
      selectedPlan === (
        summary.pending_plan ||
        summary.plan
      ) &&
      selectedInterval === (
        summary.pending_billing_interval ||
        summary.billing_interval
      );

    if (matchesPendingChange) return;

    if (!planChanges && !intervalChanges) return;

    if (
      !hasPendingChange &&
      planChanges &&
      intervalChanges
    ) {
      setError(
        'Change the plan and billing interval separately. Complete one change before starting the other.'
      );
      return;
    }

    if (
      selectedPlan === 'family' &&
      summary.active_child_count > PLAN_DETAILS.family.limit
    ) {
      setError(
        'Family supports up to 3 active children. Deactivate children until the family has 3 or fewer before downgrading.'
      );
      return;
    }

    try {
      setBusyAction('change');
      setError('');
      setMessage('');

      const data = await invokeFunction(
        'change_stripe_subscription',
        {
          plan: selectedPlan,
          billingInterval: selectedInterval,
          requestId: newRequestId(),
        },
        'Unable to change the Stripe subscription.'
      );

      if (data?.changeType === 'immediate') {
        setMessage(
          'Your upgrade was submitted and will appear here as soon as Stripe confirms it.'
        );
      } else {
        setMessage(
          `Your change is scheduled for ${formatDate(data?.effectiveAt)}.`
        );
      }

      await loadSummary({ quiet: true });
      window.setTimeout(
        () => loadSummary({ quiet: true }),
        1500
      );
    } catch (changeError) {
      console.error('Stripe subscription change error:', changeError);
      setError(
        changeError?.message ||
          'Unable to change the Stripe subscription.'
      );
    } finally {
      setBusyAction('');
    }
  };

  const handleCancelScheduledChange = async () => {
    if (busyAction || !summary) return;

    const confirmed = window.confirm(
      'Cancel this scheduled subscription change? Your current plan and billing interval will remain active.'
    );

    if (!confirmed) return;

    try {
      setBusyAction('cancel-scheduled-change');
      setError('');
      setMessage('');

      const data = await invokeFunction(
        'change_stripe_subscription',
        {
          action: 'cancel_scheduled_change',
          plan: summary.plan,
          billingInterval: summary.billing_interval,
          requestId: newRequestId(),
        },
        'Unable to cancel the scheduled subscription change.'
      );

      if (data?.changeType !== 'scheduled_change_canceled') {
        throw new Error(
          'Stripe did not confirm the scheduled change cancellation.'
        );
      }

      setMessage(
        'Your scheduled change was canceled. Your current subscription remains active.'
      );

      await loadSummary({ quiet: true });
      window.setTimeout(
        () => loadSummary({ quiet: true }),
        1500
      );
    } catch (cancelError) {
      console.error(
        'Stripe scheduled change cancellation error:',
        cancelError
      );
      setError(
        cancelError?.message ||
          'Unable to cancel the scheduled subscription change.'
      );
    } finally {
      setBusyAction('');
    }
  };

  if (loading) {
    return (
      <Card className="p-4 sm:p-5">
        <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100">
          MyPiggy Subscription
        </h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-slate-300">
          Loading billing information...
        </p>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card className="p-4 sm:p-5">
        <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100">
          MyPiggy Subscription
        </h2>
        <p className="mt-2 text-sm text-red-700 dark:text-red-300" role="alert">
          {error || 'Billing information is unavailable.'}
        </p>
        <div className="mt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => loadSummary()}
          >
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  const canManage = summary.can_manage_billing === true;
  const nativePlatform = Capacitor.getPlatform();
  const isNativeParent = nativePlatform !== 'web';
  const isAndroidParent = nativePlatform === 'android';
  const isIOSParent = nativePlatform === 'ios';
  const canManageHere = canManage && !isNativeParent && summary.billing_provider !== 'google_play';
  const canStartStripePaidNow = canManageHere && summary.billing_provider === 'stripe' &&
    summary.provider_subscription_status === 'trialing' && summary.effective_status === 'trial' &&
    !summary.is_cancellation_scheduled && !summary.pending_plan && !summary.pending_billing_interval;
  const hasUsableSubscription =
    summary.has_provider_subscription &&
    !TERMINAL_PROVIDER_STATUSES.has(
      summary.provider_subscription_status
    );
  const hasPendingChange = Boolean(
    summary.pending_plan || summary.pending_billing_interval
  );
  const currentPlan = PLAN_DETAILS[summary.plan];
  const selectedPlanDetails = PLAN_DETAILS[selectedPlan];
  const downgradeBlocked =
    selectedPlan === 'family' &&
    summary.active_child_count > PLAN_DETAILS.family.limit;
  const planChanges = selectedPlan !== summary.plan;
  const intervalChanges =
    selectedInterval !== summary.billing_interval;
  const matchesCurrentSubscription =
    !planChanges && !intervalChanges;
  const simultaneousChange =
    !hasPendingChange &&
    planChanges &&
    intervalChanges;
  const matchesPendingChange =
    hasPendingChange &&
    selectedPlan === (
      summary.pending_plan ||
      summary.plan
    ) &&
      selectedInterval === (
        summary.pending_billing_interval ||
        summary.billing_interval
      );
  const pendingImmediateUpgradeBlocked =
    hasPendingChange &&
    ((summary.plan === 'family' && selectedPlan === 'family_plus' && !intervalChanges) ||
      (summary.billing_interval === 'monthly' && selectedInterval === 'annual' && !planChanges));
  const selectedSubscriptionLabel =
    `${selectedPlanDetails?.name || 'Selected plan'}, ${
      formatStatus(selectedInterval)
    } billing`;
  let selectionGuidance = '';

  if (summary.is_cancellation_scheduled) {
    selectionGuidance =
      `This subscription is scheduled to cancel on ${
        formatDate(summary.current_period_ends_at)
      }. Open Manage Billing to review or reactivate it.`;
  } else if (downgradeBlocked) {
    selectionGuidance =
      `MyPiggy Family allows 3 active children. This family currently has ${
        summary.active_child_count
      }.`;
  } else if (matchesPendingChange) {
    selectionGuidance =
      `This subscription change is already scheduled for ${
        formatDate(summary.pending_change_effective_at)
      }.`;
  } else if (matchesCurrentSubscription && hasPendingChange) {
    selectionGuidance =
      'This is your current subscription. To keep it, cancel the scheduled change above.';
  } else if (matchesCurrentSubscription) {
    selectionGuidance =
      'This is your current subscription. Select a different plan or billing interval to make a change.';
  } else if (pendingImmediateUpgradeBlocked) {
    selectionGuidance =
      'Cancel the scheduled change first. A Family Plus upgrade with the current billing interval takes effect immediately.';
  } else if (simultaneousChange) {
    selectionGuidance =
      'Change the plan and billing interval separately.';
  } else if (hasPendingChange) {
    selectionGuidance =
      `This will replace the scheduled change with ${
        selectedSubscriptionLabel
      } on ${formatDate(summary.pending_change_effective_at)}.`;
  } else if (
    (summary.plan === 'family' && selectedPlan === 'family_plus' && !intervalChanges) ||
    (summary.billing_interval === 'monthly' && selectedInterval === 'annual' && !planChanges)
  ) {
    selectionGuidance = 'This change takes effect immediately and starts a new billing period.';
  } else {
    selectionGuidance =
      `This change to ${selectedSubscriptionLabel} will take effect after the current billing period.`;
  }

  const changeDisabled =
    Boolean(busyAction) ||
    summary.is_cancellation_scheduled ||
    downgradeBlocked ||
    pendingImmediateUpgradeBlocked ||
    simultaneousChange ||
    matchesPendingChange ||
    matchesCurrentSubscription;

  return (
    <Card className="p-4 sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="sm:max-w-xl">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100">
              MyPiggy Subscription
            </h2>

            <InfoTooltip compact label="About MyPiggy subscription access">Only the Family Organizer can start, change, or cancel the family’s subscription and update payment details. Other parents or guardians can view subscription status. Children do not have access to billing.</InfoTooltip>
          </div>
          <p className="text-sm text-gray-600 dark:text-slate-300">
            {canManage
              ? 'View and manage your family’s MyPiggy plan, billing cycle, and payment details.'
              : 'View your family’s MyPiggy plan and subscription status. Only the Family Organizer can manage billing.'}
          </p>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <span className="inline-flex self-start rounded-full bg-blue-100 dark:bg-blue-950/50 px-3 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300">
            {getPrimaryPill(summary)}
          </span>
          {hasPendingChange && (
            <span className="inline-flex self-start rounded-full bg-indigo-100 dark:bg-indigo-950/50 px-3 py-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
              Change Scheduled
            </span>
          )}
          {summary.is_cancellation_scheduled && (
            <span className="inline-flex self-start rounded-full bg-amber-100 dark:bg-amber-950/50 px-3 py-1 text-xs font-semibold text-amber-800 dark:text-amber-200">
              Cancellation Scheduled
            </span>
          )}
        </div>
      </div>

      <div
        role="status"
        className={`mt-4 rounded-lg border px-4 py-3 text-sm font-medium ${
          summary.effective_status === 'grace' ||
          summary.is_cancellation_scheduled
            ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-200'
            : summary.effective_status === 'expired'
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/35 dark:text-red-300'
              : 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950/35 dark:text-blue-200'
        }`}
      >
        {getPersistentMessage(summary)}
      </div>

      {canStartStripePaidNow && (
        <Button type="button" variant="primary" onClick={handleStartPaidNow}
          disabled={Boolean(busyAction)}>
          {busyAction === 'start-paid' ? 'Starting Paid Billing…' : 'Start Paid Billing Now'}
        </Button>
      )}

      {!canManage && summary.family_organizer_name && (
        <p className="mt-3 text-sm text-gray-600 dark:text-slate-300">
          <span className="font-semibold">Family Organizer:</span>{' '}
          {summary.family_organizer_name}
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/35 px-4 py-3 text-sm font-medium text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {message && (
        <div
          role="status"
          className="mt-4 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/35 px-4 py-3 text-sm font-medium text-green-700 dark:text-green-300"
        >
          {message}
        </div>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-300">
            Current plan
          </dt>
          <dd className="mt-1 font-extrabold text-gray-900 dark:text-slate-100">
            {currentPlan?.name || 'Free trial'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-300">
            Billing
          </dt>
          <dd className="mt-1 font-extrabold text-gray-900 dark:text-slate-100">
            {summary.billing_interval
              ? formatStatus(summary.billing_interval)
              : 'Not started'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-300">
            Active children
          </dt>
          <dd className="mt-1 font-extrabold text-gray-900 dark:text-slate-100">
            {summary.active_child_count} of {summary.active_child_limit}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-300">
            {summary.current_period_ends_at
              ? 'Current period ends'
              : 'Trial ends'}
          </dt>
          <dd className="mt-1 font-extrabold text-gray-900 dark:text-slate-100">
            {formatDate(
              summary.current_period_ends_at || summary.trial_ends_at
            )}
          </dd>
        </div>
      </dl>

      <div className="mt-5 overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700">
        <table className="w-full text-left text-sm text-gray-900 dark:text-slate-100">
          <caption className="sr-only">Subscription pricing in USD</caption>
          <thead className="border-y border-gray-200 bg-gray-50 text-gray-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
            <tr>
              <th scope="col" className="px-3 py-2 font-bold sm:px-4">Available plans</th>
              <th scope="col" className="px-3 py-2 text-right font-bold sm:px-4">Monthly</th>
              <th scope="col" className="px-3 py-2 text-right font-bold sm:px-4">Annual</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
            {Object.entries(PLAN_DETAILS).map(([planId, plan]) => (
              <tr key={planId}>
                <th scope="row" className="px-3 py-3 font-extrabold sm:px-4">
                  {plan.name.replace('MyPiggy ', '')}
                  <span className="mt-0.5 block text-xs font-normal text-gray-500 dark:text-slate-300">
                    Up to {plan.limit} kids
                  </span>
                </th>
                <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums sm:px-4">
                  {plan.monthly.replace('/month', '')}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums sm:px-4">
                  {plan.annual.replace('/year', '')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="border-t border-gray-200 px-3 py-3 text-xs text-gray-600 dark:border-slate-700 dark:text-slate-300 sm:px-4">
          Prices in USD. Final price and any applicable taxes are shown at checkout.
        </p>
      </div>

      {summary.is_cancellation_scheduled && (
        <p className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-950/35 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          This subscription will cancel on {formatDate(summary.current_period_ends_at)}. Open {summary.billing_provider === 'google_play'
            ? 'Manage in Google Play'
            : summary.billing_provider === 'apple' ? 'Manage in the App Store' : 'Manage Billing'} to review it.
        </p>
      )}

      {hasPendingChange && (
        <div className="mt-4 flex flex-col gap-3 rounded-lg bg-blue-50 dark:bg-blue-950/35 px-4 py-3 text-sm text-blue-800 dark:text-blue-200 sm:flex-row sm:items-center sm:justify-between">
          <p>
            Scheduled for {formatDate(summary.pending_change_effective_at)}:{' '}
            {summary.pending_plan
              ? PLAN_DETAILS[summary.pending_plan]?.name
              : currentPlan?.name}
            {summary.pending_billing_interval
              ? `, ${formatStatus(summary.pending_billing_interval)} billing`
              : ''}.
          </p>

          {canManageHere && !summary.is_cancellation_scheduled && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancelScheduledChange}
              disabled={Boolean(busyAction)}
            >
              {busyAction === 'cancel-scheduled-change'
                ? 'Canceling Change...'
                : 'Cancel Scheduled Change'}
            </Button>
          )}
        </div>
      )}

      {canManage && isAndroidParent &&
        (!summary.billing_provider || summary.billing_provider === 'google_play') && (
        <React.Suspense fallback={<p role="status">Loading Google Play plans…</p>}>
          <PlayBillingPanel summary={summary} onRefresh={refreshAfterProviderPurchase} />
        </React.Suspense>
      )}

      {canManage && isIOSParent && (
        <React.Suspense fallback={<p role="status">Loading App Store plans…</p>}>
          <AppleStoreKitPanel
            summary={summary}
            onRefresh={refreshAfterProviderPurchase}
          />
        </React.Suspense>
      )}

      {isAndroidParent && summary.billing_provider && summary.billing_provider !== 'google_play' && (
        <p className="mt-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/35 dark:text-blue-200" role="status">
          Your subscription is managed through {summary.billing_provider === 'stripe'
            ? 'Stripe'
            : summary.billing_provider === 'apple' ? 'the App Store' : 'another billing provider'}.
          {' '}Your family’s subscription status is shown above.
        </p>
      )}

      {canManageHere && !hasUsableSubscription && (
        <div className="mt-6 border-t border-gray-200 dark:border-slate-700 pt-5">
          <h3 className="font-semibold text-gray-900 dark:text-slate-100">
            {summary.has_provider_subscription
              ? 'Subscribe again'
              : 'Choose a plan'}
          </h3>

          <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">Checkout charges for your selected plan now and ends unused trial days.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {Object.entries(PLAN_DETAILS).map(([planId, plan]) => (
              <button
                key={planId}
                type="button"
                onClick={() => setSelectedPlan(planId)}
                aria-pressed={selectedPlan === planId}
                disabled={Boolean(busyAction)}
                className={`rounded-xl border p-4 text-left transition disabled:opacity-60 ${
                  selectedPlan === planId
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/35 ring-1 ring-blue-500'
                    : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900'
                }`}
              >
                <span className="block font-semibold text-gray-900 dark:text-slate-100">
                  {plan.name}
                </span>
                <span className="mt-1 block text-sm text-gray-600 dark:text-slate-300">
                  Up to {plan.limit} active children
                </span>
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <label className="block sm:w-64">
              <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">
                Billing interval
              </span>
              <select
                value={selectedInterval}
                onChange={(event) => setSelectedInterval(event.target.value)}
                disabled={Boolean(busyAction)}
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-gray-900 dark:text-slate-100 disabled:opacity-60"
              >
                <option value="monthly">
                  Monthly — {selectedPlanDetails.monthly}
                </option>
                <option value="annual">
                  Annual — {selectedPlanDetails.annual}
                </option>
              </select>
            </label>

            <Button
              type="button"
              variant="primary"
              onClick={handleCheckout}
              disabled={Boolean(busyAction)}
            >
              {busyAction === 'checkout'
                ? 'Opening Checkout...'
                : summary.has_provider_subscription
                  ? 'Subscribe Again'
                  : 'Continue to Checkout'}
            </Button>
          </div>
        </div>
      )}

      {canManageHere && hasUsableSubscription && (
        <div className="mt-4 border-t border-gray-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex justify-end">              <Button
                type="button"
                variant="secondary"
                onClick={handlePortal}
                disabled={Boolean(busyAction)}
              >
                {busyAction === 'portal'
                  ? 'Opening Billing...'
                  : summary.is_cancellation_scheduled
                    ? 'Manage or Restart Billing'
                    : summary.effective_status === 'grace'
                      ? 'Update Payment Method'
                      : 'Manage Billing'}
              </Button></div>
          <details>
            <summary className="w-fit rounded-lg border border-gray-200 px-3 py-2 text-sm font-extrabold text-blue-700 dark:border-slate-600 dark:text-blue-300">Change Subscription</summary>
            <div className="mt-3">

          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">
                  Plan
                </span>
                <select
                  value={selectedPlan}
                  onChange={(event) => {
                    setSelectedPlan(event.target.value);
                    setError('');
                  }}
                  disabled={Boolean(busyAction) || summary.is_cancellation_scheduled}
                  className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-gray-900 dark:text-slate-100 disabled:opacity-60"
                >
                  <option value="family">MyPiggy Family</option>
                  <option value="family_plus">MyPiggy Family Plus</option>
                </select>
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">
                  Billing interval
                </span>
                <select
                  value={selectedInterval}
                  onChange={(event) => {
                    setSelectedInterval(event.target.value);
                    setError('');
                  }}
                  disabled={Boolean(busyAction) || summary.is_cancellation_scheduled}
                  className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-gray-900 dark:text-slate-100 disabled:opacity-60"
                >
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="primary"
                onClick={handleSubscriptionChange}
                disabled={changeDisabled}
              >
                {busyAction === 'change'
                  ? 'Saving Change...'
                  : hasPendingChange
                    ? 'Replace Scheduled Change'
                    : 'Change Subscription'}
              </Button>

            </div>
          </div>

          <p
            role="status"
            className="mt-3 text-xs font-medium text-amber-700 dark:text-amber-300"
          >
            {selectionGuidance}
          </p>

          <p className="mt-3 text-xs text-gray-500 dark:text-slate-300">
            Family Plus upgrades and monthly-to-annual changes take effect immediately. Downgrades and annual-to-monthly changes take effect after the current period.
          </p>
            </div>
          </details>
        </div>
      )}
    </Card>
  );
}

